import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'

import { AppAvatar } from '@src/app/components'
import type { Identity } from '@src/service/model'
import IconButton from '@ui/Button/IconButton'
import Typography from '@ui/Typography'
import { ListenerIcon, XIcon } from '@ui/icons/tint/12/general'

import * as styles from './chip.css'

interface ChipProps {
  size: 24 | 30
  identity: Identity
  focused?: boolean
  onRemove: () => void
  className?: string
  isListener?: boolean
}

const Chip = function ({
  size,
  identity,
  focused,
  onRemove,
  className,
  isListener,
}: ChipProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [hovered, setHovered] = useState(false)
  const [width, setWidth] = useState<number | null>(null)

  const name = (
    <Typography
      variant={size === 24 ? 'caption1' : 'callout'}
      color={focused ? 'inherit' : 'textPrimary'}
      nowrap
    >
      {identity.name}
    </Typography>
  )

  const handleMouseEnter = () => {
    if (ref.current) {
      setWidth(ref.current.offsetWidth)
      setHovered(true)
    }
  }

  const handleMouseLeave = () => {
    setHovered(false)
  }

  return (
    <div
      ref={ref}
      className={cx(styles.root({ size: size }), className, {
        [styles.focused]: focused,
      })}
      style={{ width: width ?? undefined }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {identity ? (
        <div className={styles.chip}>
          {identity.pictureSymbol ? (
            <Typography variant="emoji" className={styles.symbol}>
              {identity.pictureSymbol}
            </Typography>
          ) : (
            <AppAvatar size={16} identity={identity} />
          )}
          {name}
          {isListener ? <ListenerIcon /> : null}
          {hovered || focused ? (
            <IconButton
              icon={<XIcon className={styles.removeIcon} />}
              aria-label="Remove"
              size="xxs"
              className={styles.remove}
              onClick={onRemove}
            />
          ) : null}
        </div>
      ) : (
        name
      )}
    </div>
  )
}

export default observer(Chip)
